import type { JsonFormsCore } from '@jsonforms/core';
import type {
  AnnotatedJsonSchema,
  EntityDetailError,
} from '@web-config-app/core';

/**
 * Ajv `ErrorObject.keyword` tag stamped on every injected error so
 * downstream filtering can tell them apart from real Ajv-native
 * validation errors. Implementation detail of this transform — no
 * external consumer should match on it, so it's intentionally not
 * exported from the package index.
 */
const API_ERROR_KEYWORD = 'apiValidation';

/**
 * The Ajv `ErrorObject` shape JSON Forms keeps in `JsonFormsCore.errors`.
 * Derived from the core type instead of imported directly because Ajv's
 * type isn't re-exported from `@jsonforms/core`.
 */
type AjvErrorObject = NonNullable<JsonFormsCore['errors']>[number];

const buildAjvError = (
  detail: string,
  instancePath: string,
): AjvErrorObject => ({
  instancePath,
  schemaPath: '#',
  keyword: API_ERROR_KEYWORD,
  params: {},
  message: detail,
});

const toRelativeAjvError = (
  error: EntityDetailError,
  slicePrefix: string,
): AjvErrorObject | null => {
  const { fieldPath, detail } = error;
  if (!fieldPath || !fieldPath.startsWith(slicePrefix)) return null;

  const remainder = fieldPath.slice(slicePrefix.length);
  // The slice prefix must end at a path boundary — guards against e.g.
  // `/attributes/flowTemplateOther` matching the prefix `/attributes/flowTemplate`.
  if (remainder !== '' && !remainder.startsWith('/')) return null;

  return buildAjvError(detail, remainder);
};

/**
 * Property-name fallback for backend pointers that are abbreviated relative
 * to the actual schema layout (e.g. `/data/attributes/campaignNodes` when
 * the field actually lives at `attributes.flowTemplate.campaignNodes`). If
 * the leaf segment of the error's `fieldPath` matches a top-level property
 * on the form's current slice schema, we inject the error at that
 * slice-relative path so the matching control still highlights inline.
 *
 * Mirrors the same heuristic `findNavigableFormPath` uses to pick a
 * banner link target when the pointer-derived path can't be located in
 * the form's tree directly.
 */
const toPropertyNameFallbackError = (
  error: EntityDetailError,
  formSchema: AnnotatedJsonSchema | undefined,
): AjvErrorObject | null => {
  const { fieldPath, detail } = error;
  if (!fieldPath) return null;

  const segments = fieldPath.split('/').filter((segment) => segment !== '');
  const leafSegment = segments[segments.length - 1];
  if (!leafSegment) return null;

  const properties = formSchema?.properties;
  if (
    !properties ||
    !Object.prototype.hasOwnProperty.call(properties, leafSegment)
  ) {
    return null;
  }

  return buildAjvError(detail, `/${leafSegment}`);
};

/**
 * Transform API validation errors (mirrored into the user-feedback
 * store's api-validation slice) into the Ajv `ErrorObject[]` shape
 * JSON Forms accepts via its `additionalErrors` prop.
 *
 * The transform takes a `slicePrefix` representing the absolute instance
 * path the form's current data slice is rooted at (e.g.
 * `/attributes/contentData` when the user is viewing the `contentData`
 * section). Errors whose `fieldPath` falls under this prefix have their
 * paths translated to be slice-relative so JSON Forms' built-in
 * error-by-instance-path filtering can route each one to its matching
 * control. Errors outside the current slice — or backend pointers
 * abbreviated such that they don't match the schema layout — are passed
 * through a property-name fallback that checks the current slice schema
 * for a top-level property whose name matches the leaf segment of the
 * pointer; on a hit, the error is injected at that property's
 * slice-relative path. Anything still unresolved is dropped (it continues
 * to surface only in the banner).
 *
 * @param apiValidationErrors Errors mirrored from the user-feedback store.
 * @param slicePrefix Absolute instance-path prefix of the current slice.
 * @param formSchema Optional sub-schema for the slice; enables the
 *   property-name fallback for abbreviated backend pointers.
 */
/**
 * Stable empty-array reference returned for the no-errors case. JSON
 * Forms checks `state.additionalErrors !== additionalErrors` to decide
 * whether to dispatch `UPDATE_CORE` (see `core.ts` reducer); a fresh
 * `[]` literal would re-trigger that on every unrelated render.
 */
const EMPTY_AJV_ERRORS: AjvErrorObject[] = [];

export const buildApiValidationErrorObjects = (
  apiValidationErrors: EntityDetailError[],
  slicePrefix: string | undefined,
  formSchema?: AnnotatedJsonSchema,
): AjvErrorObject[] => {
  if (!apiValidationErrors.length || slicePrefix === undefined) {
    return EMPTY_AJV_ERRORS;
  }
  return apiValidationErrors
    .map(
      (error) =>
        toRelativeAjvError(error, slicePrefix) ??
        toPropertyNameFallbackError(error, formSchema),
    )
    .filter((error): error is AjvErrorObject => error !== null);
};
